import * as Linking from 'expo-linking';
import { Platform, Share } from 'react-native';
import { whatsAppHref } from '@shared/phone';
import type { CustomerAccountRow } from '../domain/customerTypes';
import { getOrCreatePortalToken } from '../infra/customerRepository';
import { showError } from '../../shared/ui/appToast';
import { formatMoney } from '../../shared/format/public-api';

export function openExternalUrl(url: string): Promise<void> {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer');
    return Promise.resolve();
  }
  return Linking.openURL(url).then(() => undefined);
}

export function buildCustomerPaymentMessage(customer: CustomerAccountRow, paymentUrl: string): string {
  return `Hola ${customer.name}, tenés ${formatMoney(customer.balance)} de deuda. Pagalo acá: ${paymentUrl}`;
}

export async function ensureCustomerPortalLink(
  customerId: string,
  portalBaseUrl: string,
  options?: { forceRefresh?: boolean }
) {
  const portalToken = await getOrCreatePortalToken(customerId, options);
  const paymentUrl = portalToken.paymentLinkUrl ?? `${portalBaseUrl}/pago/${portalToken.token}`;
  return { ...portalToken, paymentUrl };
}

function assertCustomerPortalActionAllowed(params: {
  canEdit: boolean;
  paymentLinkAllowed: boolean;
  readOnlyMessage: string;
  planMessage: string;
}): boolean {
  if (!params.canEdit) {
    showError('Modo solo lectura', params.readOnlyMessage);
    return false;
  }
  if (!params.paymentLinkAllowed) {
    showError('Plan Gratis', params.planMessage);
    return false;
  }
  return true;
}

export async function shareCustomerPaymentLink(params: {
  customer: CustomerAccountRow;
  portalBaseUrl: string;
  canEdit: boolean;
  paymentLinkAllowed: boolean;
}): Promise<void> {
  const { customer, portalBaseUrl, canEdit, paymentLinkAllowed } = params;
  if (
    !assertCustomerPortalActionAllowed({
      canEdit,
      paymentLinkAllowed,
      readOnlyMessage: 'Tu perfil puede ver clientes, pero no compartir el link de cobro.',
      planMessage: 'El link de cobro viene con un plan de pago. Mejorá el plan en Plan y facturación.',
    })
  ) {
    return;
  }
  const { paymentUrl } = await ensureCustomerPortalLink(customer.id, portalBaseUrl, { forceRefresh: true });
  await Share.share({ message: buildCustomerPaymentMessage(customer, paymentUrl) });
}

export async function sendCustomerWhatsAppReminder(params: {
  customer: CustomerAccountRow;
  portalBaseUrl: string;
  canEdit: boolean;
  paymentLinkAllowed: boolean;
}): Promise<void> {
  const { customer, portalBaseUrl, canEdit, paymentLinkAllowed } = params;
  if (
    !assertCustomerPortalActionAllowed({
      canEdit,
      paymentLinkAllowed,
      readOnlyMessage: 'Tu perfil puede ver clientes, pero no mandar recordatorios.',
      planMessage: 'Los recordatorios con link de pago vienen con un plan de pago. Mejorá el plan en Plan y facturación.',
    })
  ) {
    return;
  }
  const { paymentUrl } = await ensureCustomerPortalLink(customer.id, portalBaseUrl);
  const waUrl = whatsAppHref(customer.phone, buildCustomerPaymentMessage(customer, paymentUrl));
  if (!waUrl) {
    showError('Sin teléfono', 'Editá el cliente y cargá un teléfono para abrir WhatsApp.');
    return;
  }
  await openExternalUrl(waUrl);
}
